import React, { useState, useEffect, useCallback } from 'react';
import { updateOrdenesCompra } from '../../api/ordenesCompra';
import { Button, TextField, Box, CircularProgress, Checkbox, Autocomplete } from '@mui/material';
import { updateGanacias } from '../../api/resumenGanancias';
import { getProductosEnInventario } from '../../api/productos';

interface Producto {
  id: number;
  nombre: string;
  unidad: string;
}

const TipoClientes = [
  { id: 1, nombre: 'Consecionario' },
  { id: 2, nombre: 'Particular' },
];

const EditGanancias = React.memo(({ ganancias, onClose }: any) => {
  const [productos, setProductos] = useState<Producto[]>([]);
  const [formData, setFormData] = useState(ganancias);
  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState({
    GanRe_Pr_Id: '',
    GanRe_Sub1: '',
    GanRe_ManoObra: '', 
    GanRe_GastoAdicional: '',
    GanRe_PorceUtilidad: '',
    GanRe_TipoCliente: '',
  });

  const fetchProductos = useCallback(async () => {
    try {
      const response = await getProductosEnInventario();
      if (response.isOk && response.data) {
        const mappedProductos = response.data.map((prod: any) => ({
          id: prod.Pr_Id,
          nombre: prod.Pr_Nombre,
          unidad: prod.Un_Nombre,
        }));
        setProductos(mappedProductos);
      } else {
        setProductos([]);
      }
    } catch (error) {
      console.error('Error inesperado al cargar los productos:', error);
    }
  }, []);

  useEffect(() => {
      fetchProductos();
    }, [fetchProductos]);

    useEffect(() => {
      setFormData((prev: any) => ({
        ...prev,
        ...ganancias,
        ivaCheck: ganancias.ivaCheck ?? false, // Asegurar que sea booleano
      }));
    }, [ganancias]);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFormData((prev: any) => ({
      ...prev,
      [name]: isNaN(Number(value)) ? value : Number(value),
    }));
  };

  const validate = () => {
    let tempErrors = {
      GanRe_Pr_Id: '',
      GanRe_Sub1: '',
      GanRe_ManoObra: '', 
      GanRe_GastoAdicional: '',
      GanRe_PorceUtilidad: '',
      GanRe_TipoCliente: '',
    };
    if (formData.GanRe_Pr_Id === 0) { 
      tempErrors.GanRe_Pr_Id = 'Tienes que escoger un tipo de cliente';
    }
    if (formData.GanRe_Sub1 === null || formData.GanRe_Sub1 <= 0) {
      tempErrors.GanRe_Sub1 = 'El número tiene que ser mayor a 0';
    }
    if (formData.GanRe_ManoObra === null || formData.GanRe_ManoObra <= 0) {
      tempErrors.GanRe_ManoObra = 'El número tiene que ser mayor a 0';
    }
    // if (formData.GanRe_GastoAdicional === null || formData.GanRe_GastoAdicional <= 0) {
    //   tempErrors.GanRe_GastoAdicional = 'El número tiene que ser mayor a 0';
    // }
    if (formData.GanRe_PorceUtilidad === null || formData.GanRe_PorceUtilidad <= 0) {
      tempErrors.GanRe_PorceUtilidad = 'El número tiene que ser mayor a 0';
    }
    if (formData.GanRe_TipoCliente === 0) { 
      tempErrors.GanRe_TipoCliente = 'Tienes que escoger un tipo de cliente';
    }
    setErrors(tempErrors);
    return Object.values(tempErrors).every((x) => x === '');
  };

  const handleSubmit = async () => {
    if (!validate()) return;
    setLoading(true);
    try {
      await updateGanacias(formData.GanRe_Id, formData);
      onClose();
    } catch (error) {
      console.error('Error al actualizar las ganancias:', error);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div>
      <form>
         <Autocomplete
          options={productos}
          getOptionLabel={(option) => option.nombre + ' - ' + option.unidad}
          isOptionEqualToValue={(option, value) => option.id === value.id}
          value={productos.find((p) => p.id === formData.GanRe_Pr_Id) || null}
          onChange={(event, value) => setFormData({ ...formData, GanRe_Pr_Id: value ? value.id : 0 })}
          renderInput={(params) => <TextField {...params} label="Producto" variant="outlined" fullWidth sx={{ marginBottom: '0.8rem' }} 
          error={!!errors.GanRe_Pr_Id} 
          helperText={errors.GanRe_Pr_Id}  
          />}
        />

        <TextField
          label="Subtotal"
          name="GanRe_Sub1"
          value={formData.GanRe_Sub1}
          onChange={handleChange}
          fullWidth
          required
          sx={{ marginBottom: '0.8rem', marginTop: '1rem' }}
          size="small"
          error={!!errors.GanRe_Sub1}
          helperText={errors.GanRe_Sub1}
        />

        <TextField
          label="Mano de obra"
          name="GanRe_ManoObra"
          value={formData.GanRe_ManoObra}
          onChange={handleChange}
          fullWidth
          required
          sx={{ marginBottom: '0.8rem', marginTop: '1rem' }}
          size="small"
          error={!!errors.GanRe_ManoObra}
          helperText={errors.GanRe_ManoObra}
        />

        <TextField
          label="Gasto adicional"
          name="GanRe_GastoAdicional"
          value={formData.GanRe_GastoAdicional}
          onChange={handleChange}
          fullWidth
          required
          sx={{ marginBottom: '0.8rem', marginTop: '1rem' }}
          size="small"
        />

        <TextField
          label="Porcentaje de utilidad"
          name="GanRe_PorceUtilidad"
          value={formData.GanRe_PorceUtilidad}
          onChange={handleChange}
          fullWidth
          required
          sx={{ marginBottom: '0.8rem', marginTop: '1rem' }}
          size="small"
        />

        <Autocomplete
          options={TipoClientes}
          getOptionLabel={(option) => option.nombre}
          isOptionEqualToValue={(option, value) => option.id === value.id}
          value={TipoClientes.find((t) => t.id === formData.GanRe_TipoCliente) || null}
          onChange={(event, value) => setFormData({ ...formData, GanRe_TipoCliente: value ? value.id : 0 })}
          renderInput={(params) => <TextField {...params} label="Tipo Cliente" variant="outlined" fullWidth sx={{ marginBottom: '0.8rem' }} 
          error={!!errors.GanRe_TipoCliente} 
          helperText={errors.GanRe_TipoCliente}  
          />}
        />
        <Box display="flex" alignItems="center">
          <Checkbox
            checked={formData.ivaCheck}
            onChange={(e) => setFormData({ ...formData, ivaCheck: e.target.checked })}
          />
          <span>¿Aplicar IVA?</span>
        </Box>
        <Box display="flex" justifyContent="space-between">
          <Button onClick={handleSubmit} variant="outlined" size="small" disabled={loading}>
            {loading ? <CircularProgress size={24} /> : 'Actualizar'}
          </Button>
          <Button onClick={onClose} variant="outlined" color="error" size="small">
            Cancelar
          </Button>
        </Box>
      </form>
    </div>
  );
});

export default EditGanancias;
